import React, { useEffect, useRef } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { Icon } from '../';
import { Modal as AntModal, Button } from 'antd';
import { useTranslation } from 'react-i18next';

const Notification = ({
  id,
  type,
  message,
  actions,
  onSubmit,
  onOutsideClick,
}) => {
  const notificationRef = useRef(null);
  const { t } = useTranslation('ViewerTool');

  useEffect(() => {
    const notificationElement = notificationRef.current;
    const handleClick = function(event) {
      const isClickInside = notificationElement.contains(event.target);

      if (!isClickInside) {
        onOutsideClick();
      }
    };

    document.addEventListener('mousedown', handleClick);

    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [onOutsideClick]);

  const iconsByType = {
    error: {
      icon: 'info',
      color: 'text-red-700',
    },
    warning: {
      icon: 'notificationwarning-diamond',
      color: 'text-yellow-500',
    },
    info: {
      icon: 'info',
      color: 'text-primary-main',
    },
    success: {
      icon: 'info',
      color: 'text-green-500',
    },
  };

  const getIconData = () => {
    return (
      iconsByType[type] || {
        icon: '',
        color: '',
      }
    );
  };

  const { icon, color } = getIconData();

  return (
    <AntModal open={true} footer={null} title={<h3 className="flex grow items-center"><Icon name={icon} className={classnames('w-5 mr-2', color)} /><span>{t(message)}</span></h3>}>
      <div ref={notificationRef}>
        <div className="flex justify-end mt-4">
          {actions.map((action, index) => {
            const isFirst = index === 0;
            const isPrimary = action.type === 'primary';

            return (
              <Button
                data-cy={action.id}
                key={index}
                type={isPrimary ? 'primary' : 'default'}
                className={classnames({ 'ml-2': !isFirst })}
                color={isPrimary ? 'primary' : undefined}
                onClick={() => {
                  onSubmit(action.value);
                }}
              >
                {t(action.text)}
              </Button>
            );
          })}
        </div>
      </div>
    </AntModal>
  );
};

Notification.defaultProps = {
  type: 'info',
  onOutsideClick: () => {},
};

Notification.propTypes = {
  type: PropTypes.oneOf(['error', 'warning', 'info', 'success']),
  message: PropTypes.string.isRequired,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
      type: PropTypes.oneOf(['primary', 'secondary', 'cancel']).isRequired,
    })
  ).isRequired,
  onSubmit: PropTypes.func.isRequired,
  /** Can be used as a callback to dismiss the notification for clicks that occur outside of it */
  onOutsideClick: PropTypes.func,
};

export default Notification;
